import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { apiGet, apiPost, apiDelete } from '../utils/apiUtils';
import { FiArrowLeft, FiPlus, FiCheckCircle, FiCircle, FiX, FiClock, FiFileText, FiTrash2, FiChevronDown, FiChevronRight, FiChevronLeft, FiBarChart2, FiCode, FiCheck } from 'react-icons/fi';
import Editor from '@monaco-editor/react';
import TestResultsModal from './TestResultsModal';

/**
 * @file TestManager.jsx
 * @description Admin interface component for managing a specific test's curriculum.
 *              Provides the ability to add, edit, and remove MCQ and Coding questions.
 * @module components/TestManager
 */

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component TestManager
 * @description Renders the comprehensive admin toolkit for a test's question repository.
 * 
 * @param {Object} props
 * @param {Object} props.test - The active test object being managed.
 * @param {Function} props.onBack - Callback to return to the batch details view.
 * @returns {JSX.Element} The rendered Test Manager view.
 */
const TestManager = ({ test, onBack }) => {
    // ── Component State ──
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);

    /* ══════════════════════════════════════════════════════════════
     * SKELETON LOADER
     * ══════════════════════════════════════════════════════════════ */

    const TestManagerSkeleton = () => (
        <div className="space-y-10 animate-pulse">
            {(test.test_type === 'mcq' || test.test_type === 'mixed') && (
                <div className="space-y-4">
                    {test.test_type === 'mixed' && (
                        <div className="flex items-center space-x-3 px-1">
                            <div className="h-4 w-48 bg-emerald-100 rounded" />
                            <div className="flex-1 h-0.5 bg-emerald-100 rounded-full" />
                        </div>
                    )}
                    <div className="bg-white rounded overflow-hidden border-l-4 border-l-emerald-100 divide-y divide-gray-100">
                        {[1, 2, 3, 4, 5].map(i => (
                            <div key={i} className="flex items-center justify-between px-6 py-4">
                                <div className="flex-1 grid grid-cols-[40px,1fr,100px,120px,80px] items-center gap-4">
                                    <div className="h-4 w-6 bg-gray-100 rounded" />
                                    <div className="h-4 w-2/3 bg-gray-100 rounded" />
                                    <div />
                                    <div />
                                    <div className="h-4 w-16 bg-gray-100 rounded mx-auto" />
                                </div>
                                <div className="flex items-center space-x-4 ml-6">
                                    <div className="h-8 w-8 bg-gray-50 rounded" />
                                    <div className="h-5 w-5 bg-gray-50 rounded" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {(test.test_type === 'code' || test.test_type === 'mixed') && (
                <div className="space-y-4">
                    {test.test_type === 'mixed' && (
                        <div className="flex items-center space-x-3 px-1">
                            <div className="h-4 w-48 bg-emerald-100 rounded" />
                            <div className="flex-1 h-0.5 bg-emerald-100 rounded-full" />
                        </div>
                    )}
                    <div className="bg-white rounded overflow-hidden border-l-4 border-l-emerald-100 divide-y divide-gray-100">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="flex items-center justify-between px-6 py-4">
                                <div className="flex-1 grid grid-cols-[40px,1fr,100px,120px,80px] items-center gap-4">
                                    <div className="h-4 w-6 bg-gray-100 rounded" />
                                    <div className="h-4 w-1/2 bg-gray-100 rounded" />
                                    <div className="h-3 w-16 bg-gray-100 rounded" />
                                    <div className="h-3 w-24 bg-gray-100 rounded" />
                                    <div className="h-4 w-16 bg-gray-100 rounded mx-auto" />
                                </div>
                                <div className="flex items-center space-x-4 ml-6">
                                    <div className="h-8 w-8 bg-gray-50 rounded" />
                                    <div className="h-5 w-5 bg-gray-50 rounded" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    // Add MCQ Modal State
    const [showAddMcq, setShowAddMcq] = useState(false);
    const [questionText, setQuestionText] = useState('');
    const [optionA, setOptionA] = useState('');
    const [optionB, setOptionB] = useState('');
    const [optionC, setOptionC] = useState('');
    const [optionD, setOptionD] = useState('');
    const [correctOptionIndex, setCorrectOptionIndex] = useState(0); // 0 corresponds to A, 1 to B, 2 to C, 3 to D
    const [marks, setMarks] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [deletingQuestionId, setDeletingQuestionId] = useState(null);
    const [expandedQ, setExpandedQ] = useState(null); // id of expanded question
    const [currentMcqPage, setCurrentMcqPage] = useState(1);
    const [currentCodePage, setCurrentCodePage] = useState(1);
    const [showResultsModal, setShowResultsModal] = useState(false);

    // Code Snippet State
    const [showQuestionCode, setShowQuestionCode] = useState(false);
    const [questionCode, setQuestionCode] = useState('');
    const [showOptionCodes, setShowOptionCodes] = useState(false);
    const [optionCodes, setOptionCodes] = useState(['', '', '', '']);

    // Create a local test state to instantly update the UI when results are published
    const [localTest, setLocalTest] = useState(test);
    const [expandedQuestions, setExpandedQuestions] = useState(new Set());
    const QUESTIONS_PER_PAGE = 10;

    /**
     * Toggles the expansion state of a specific question to show/hide its options and code snippets.
     * Uses an accordion-style behavior (only one expanded at a time).
     * 
     * @param {number|string} id - The unique identifier of the question to toggle.
     */
    const toggleQuestion = (id) => {
        setExpandedQuestions(prev => {
            const next = new Set();
            // If the clicked question wasn't already the only one expanded, expand it
            if (!prev.has(id)) {
                next.add(id);
            }
            // Otherwise, next remains empty (collapses the current one)
            return next;
        });
    };

    const handleEditorDidMount = (editor, monaco) => {
        // No custom theme definition needed, using standard vs-dark
    };

    // --- Code Question Modal State ---
    const [showQuestionSearch, setShowQuestionSearch] = useState(false);
    const [codeQuestions, setCodeQuestions] = useState([]);

    /* ══════════════════════════════════════════════════════════════
     * EFFECTS & HANDLERS
     * ══════════════════════════════════════════════════════════════ */

    useEffect(() => {
        // Set dynamic page title while managing a test
        document.title = `SimpleCode | Manage ${test.title || 'Test'}`;
        
        return () => {
            // Revert or let parent component handle it (BatchDetails)
            document.title = "SimpleCode";
        };
    }, [test]);


    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                setLoading(true);
                const res = await apiGet(`/tests/${test.id}/questions`);
                if (res.ok) {
                    const data = await res.json();
                    setQuestions(data.mcq_questions || []);
                    setCodeQuestions(data.code_questions || []); // New backend payload
                }
            } catch (err) {
                console.error('Failed to fetch questions:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchQuestions();
    }, [test.id]);

    const handleAddMcq = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const optionsArray = [optionA, optionB, optionC, optionD];

            // Validate options aren't empty
            if (optionsArray.some(opt => !opt.trim())) {
                alert("Please fill in all 4 options.");
                setSubmitting(false);
                return;
            }

            const payload = {
                question_text: questionText,
                options: optionsArray,
                correct_option_index: correctOptionIndex,
                marks: Number(marks),
                order_index: questions.length, // Add to end
                question_code: showQuestionCode ? questionCode : null,
                option_codes: showOptionCodes ? optionCodes : null
            };

            const response = await apiPost(`/tests/${test.id}/questions/mcq`, payload);

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to add question');
            }

            const data = await response.json();
            setQuestions([...questions, data.question]);

            // Reset modal
            setShowAddMcq(false);
            setQuestionText('');
            setOptionA('');
            setOptionB('');
            setOptionC('');
            setOptionD('');
            setMarks(1);
            setShowQuestionCode(false);
            setQuestionCode('');
            setShowOptionCodes(false);
            setOptionCodes(['', '', '', '']);
        } catch (err) {
            console.error('Add MCQ error:', err);
            alert(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const PaginationControls = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage, label }) => {
        if (totalPages <= 1) return null;

        const startItem = (currentPage - 1) * itemsPerPage + 1;
        const endItem = Math.min(currentPage * itemsPerPage, totalItems);

        return (
            <div className="border-t border-gray-300 bg-white px-6 py-4 rounded-b">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <span className="text-sm font-medium text-muted-foreground">
                        Showing <span className="text-foreground font-semibold">{totalItems > 0 ? startItem : 0}-{endItem}</span> of <span className="text-foreground font-semibold">{totalItems}</span> {label}
                    </span>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="flex items-center justify-center w-6 h-6 text-emerald-700 font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        >
                            <FiChevronLeft className="w-4 h-4" />
                        </button>
                        <div className="flex items-center gap-1.5 px-2">
                            <span className="font-semibold text-sm text-foreground">{currentPage}</span>
                            <span className="text-muted-foreground text-xs font-medium px-1">/</span>
                            <span className="text-muted-foreground text-sm font-medium">{totalPages}</span>
                        </div>
                        <button
                            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="flex items-center justify-center w-6 h-6 text-emerald-700 font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        >
                            <FiChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const ImprovedCodeSnippet = ({ code, language = 'cpp', maxHeight = '300px', filename: customFilename }) => {
        const lineCount = code.split('\n').length;
        const lineHeight = 24;
        const paddingOffset = 42; 
        const minHeight = 85;
        const estimatedHeight = Math.max(minHeight, Math.min(parseInt(maxHeight), (lineCount * lineHeight) + paddingOffset));
             
        const extMap = {
            'cpp': 'cpp',
            'java': 'java',
            'python': 'py',
            'javascript': 'js',
            'c': 'c'
        };
    
        const filename = customFilename || `main.${extMap[language.toLowerCase()] || language}`;
    
        return (
            <div className="improved-code-snippet flex flex-col rounded border border-neutral-700 bg-neutral-800 overflow-hidden font-mono group w-full mb-4 cursor-not-allowed">
                <style>
                    {`
                        .improved-code-snippet .monaco-editor .view-line, 
                        .improved-code-snippet .monaco-editor .view-lines,
                        .improved-code-snippet .monaco-editor .margin-view-overlays,
                        .improved-code-snippet .monaco-editor .view-content {
                            cursor: not-allowed !important;
                        }
                    `}
                </style>
                <div className="bg-neutral-800/50 px-2 py-2 border-b border-neutral-700">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="flex space-x-1.5 cursor-not-allowed">
                                <div className="w-3 h-3 rounded-full bg-[#ff5f56]"></div>
                                <div className="w-3 h-3 rounded-full bg-[#ffbd2e]"></div>
                                <div className="w-3 h-3 rounded-full bg-[#27c93f]"></div>
                            </div>
                            <span className="text-xs text-gray-400 font-mono cursor-not-allowed">
                                {filename}
                            </span>
                        </div>
                    </div>
                </div>
    
                <div style={{ height: estimatedHeight + 'px' }} className="py-2 cursor-not-allowed">
                    <Editor
                        height="100%"
                        theme="vs-dark"
                        language={language.toLowerCase()}
                        value={code}
                        options={{
                            readOnly: true,
                            domReadOnly: true,
                            fontSize: 13,
                            lineHeight: lineHeight,
                            minimap: { enabled: false },
                            scrollBeyondLastLine: false,
                            lineNumbers: 'on',
                            lineNumbersMinChars: 3,
                            lineDecorationsWidth: 40,
                            fontFamily: 'Fira Code, Monaco, Menlo, "Ubuntu Mono", monospace',
                            renderLineHighlight: 'none',
                            scrollbar: {
                                verticalScrollbarSize: 6,
                                horizontalScrollbarSize: 6,
                                useShadows: false
                            },
                            padding: { top: 16, bottom: 16 },
                            folding: false,
                            automaticLayout: true,
                            contextmenu: false
                        }}
                    />
                </div>
            </div>
        );
    };

    const handleDeleteQuestion = async (questionId, isCodeQuestion = false) => {
        if (!window.confirm('Delete this question from the test?')) return;
        setDeletingQuestionId(questionId);
        try {
            const endpoint = isCodeQuestion
                ? `/tests/${test.id}/questions/code/${questionId}`
                : `/tests/${test.id}/questions/${questionId}`;

            const response = await apiDelete(endpoint);
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to delete question');
            }

            if (isCodeQuestion) {
                setCodeQuestions(codeQuestions.filter(q => q.id !== questionId));
            } else {
                setQuestions(questions.filter(q => q.id !== questionId));
            }

        } catch (err) {
            console.error('Delete question error:', err);
            alert(err.message);
        } finally {
            setDeletingQuestionId(null);
        }
    };

    return (
        <div className="space-y-6">
            <section className="bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 mt-2 pb-2">
                <div className="max-w-8xl mx-auto">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <button
                            onClick={onBack}
                            className="text-muted-foreground flex items-center space-x-2 hover:text-emerald-700 transition-all text-md font-medium whitespace-nowrap flex-shrink-0"
                        >
                            <FiArrowLeft />
                            <span>Back to All Assessments</span>
                        </button>
                    </div>
                </div>
            </section>
            <div className="flex items-start justify-between text-left bg-emerald-600 rounded p-4">
                <div className="flex-1">
                    <h2 className="text-2xl font-bold text-white mb-1">{test.title}</h2>
                    <div className="flex items-center gap-4">
                        <p className="text-sm text-emerald-100 font-medium">{test.description || 'No description provided'}</p>
                    </div>
                </div>
                {test.duration_minutes && (
                    <div className="flex items-center gap-1 text-sm font-medium text-white">
                        <FiClock />
                        <span>{test.duration_minutes} Minutes</span>
                    </div>
                )}
            </div>

            <div className="flex items-center justify-end mt-8 mb-4">
                <div className="flex space-x-3">
                    {/* <button
                        onClick={() => setShowResultsModal(true)}
                        className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-emerald-400 transition-colors text-sm font-semibold flex items-center space-x-2 border border-emerald-500/30"
                    >
                        <FiBarChart2 /> <span>Results</span>
                    </button> */}
                    {/* <div className="w-px h-8 bg-zinc-800 mx-1 self-center"></div> */}

                    {(test.test_type === 'mcq' || test.test_type === 'mixed') && (
                        <button onClick={() => setShowAddMcq(true)} className="px-4 py-2 bg-emerald-600 rounded hover:bg-emerald-700 text-white transition-colors text-sm font-semibold flex items-center space-x-2">
                            <FiPlus /> <span>Add MCQ Question</span>
                        </button>
                    )}

                    {(test.test_type === 'code' || test.test_type === 'mixed') && (
                        <button onClick={() => setShowQuestionSearch(true)} className="px-4 py-2 bg-emerald-600 rounded hover:bg-emerald-700 text-white transition-colors text-sm font-semibold flex items-center space-x-2">
                            <FiPlus /> <span>Import Code Problem</span>
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <TestManagerSkeleton />
            ) : (questions.length === 0 && codeQuestions.length === 0) ? (
                <div className="flex flex-col items-center justify-center text-center py-8 bg-white rounded border-l-4 border-emerald-600 shadow-sm min-h-[200px]">
                    <h3 className="text-xl font-bold text-muted-foreground mb-2">No questions added yet</h3>
                    <p className="text-muted-foreground max-w-lg mx-auto">
                        Start building your assessment by adding some questions
                    </p>
                </div>
            ) : (
                <div className="space-y-10">
                    {/* Section I: MCQs */}
                    {questions.length > 0 && (
                        <div className="space-y-4">
                            {test.test_type === 'mixed' && (
                                <div className="flex items-center space-x-3 px-1">
                                    <h3 className="text-sm font-bold text-emerald-600 uppercase tracking-widest whitespace-nowrap">Section I: Multiple Choice Questions</h3>
                                    <div className="flex-1 h-0.5 rounded-full bg-emerald-600"></div>
                                </div>
                            )}
                            <div className="bg-white rounded overflow-hidden border-l-4 border-l-emerald-600">
                                <div className="divide-y divide-gray-300">
                                    {questions
                                        .slice((currentMcqPage - 1) * QUESTIONS_PER_PAGE, currentMcqPage * QUESTIONS_PER_PAGE)
                                        .map((q, idx) => {
                                            const isExpanded = expandedQuestions.has(q.id);
                                            const displayIdx = (currentMcqPage - 1) * QUESTIONS_PER_PAGE + idx + 1;
                                            return (
                                                <div key={`mcq_${q.id}`} className="group">
                                                    {/* Main Row */}
                                                    <div
                                                        className="flex items-center justify-between px-6 py-2 transition-all cursor-pointer"
                                                        onClick={() => toggleQuestion(q.id)}
                                                    >
                                                        <div className="flex-1 grid grid-cols-[40px,1fr,100px,120px,80px] items-center gap-4">
                                                            <span className="text-sm font-bold text-emerald-600 text-left">Q{displayIdx}.</span>

                                                            <div className="min-w-0 pr-4">
                                                                <span className={`text-sm font-semibold text-foreground text-left truncate block`} title={q.question_text}>
                                                                    {q.question_text}
                                                                </span>
                                                            </div>

                                                            {/* Empty cells to align with Code questions difficulty/topic */}
                                                            <div />
                                                            <div />

                                                            <div className="flex justify-center">
                                                                <span className="text-sm font-bold text-slate-700">{q.marks} Mark{q.marks > 1 ? 's' : ''}</span>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center space-x-4 ml-6">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteQuestion(q.id, false);
                                                                }}
                                                                disabled={deletingQuestionId === q.id}
                                                                className="p-2 text-muted-foreground hover:text-red-600 transition-all rounded"
                                                                title="Remove Question"
                                                            >
                                                                {deletingQuestionId === q.id ? (
                                                                    <div className="w-4 h-4 bg-red-100 animate-pulse rounded" />
                                                                ) : (
                                                                    <FiTrash2 className="w-4 h-4" />
                                                                )}
                                                            </button>
                                                            <div className={`transition-all duration-300 ${isExpanded ? 'rotate-180 text-emerald-600' : 'text-slate-400'}`}>
                                                                <FiChevronDown className="text-xl" />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Expandable Preview with Smooth Transition */}
                                                    <div className={`grid transition-all duration-300 ease-in-out ${isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                                                        <div className="overflow-hidden">
                                                            <div className="px-6 pb-6 pl-[4.25rem]">
                                                                {q.question_code && (
                                                                    <div className="mt-4 w-full">
                                                                        <ImprovedCodeSnippet 
                                                                            code={q.question_code} 
                                                                            language={q.question_language || 'cpp'} 
                                                                            maxHeight="400px" 
                                                                        />
                                                                    </div>
                                                                )}

                                                                <div className="mt-6 grid grid-cols-2 gap-3 w-full">
                                                                    {[0, 1, 2, 3].map((optIdx) => {
                                                                        const opt = q.options ? q.options[optIdx] : [q.option_a, q.option_b, q.option_c, q.option_d][optIdx];
                                                                        const isCorrect = q.correct_option_index === optIdx;
                                                                        return (
                                                                            <div key={optIdx} className="space-y-3 w-full">
                                                                                <div className={`flex items-start pl-4 pr-6 py-3 text-sm rounded transition-all border ${
                                                                                    isCorrect 
                                                                                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-md' 
                                                                                        : 'bg-accent text-slate-700 border-gray-100'
                                                                                }`}>
                                                                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center mt-0.5 mr-3 shrink-0 ${
                                                                                        isCorrect ? 'bg-white' : 'bg-slate-700'
                                                                                    }`}>
                                                                                        {isCorrect ? (
                                                                                            <FiCheck className="w-3 h-3 text-emerald-600" strokeWidth={5} />
                                                                                        ) : (
                                                                                            <span className="text-[10px] font-bold text-white">{['A','B','C','D'][optIdx]}</span>
                                                                                        )}
                                                                                    </div>
                                                                                    <span className="flex-1 break-words font-medium leading-tight text-left">
                                                                                        {opt || `Option ${['A','B','C','D'][optIdx]}`}
                                                                                    </span>
                                                                                </div>

                                                                                {q.option_codes && q.option_codes[optIdx] && (
                                                                                    <ImprovedCodeSnippet 
                                                                                        code={q.option_codes[optIdx]} 
                                                                                        language={q.question_language || 'cpp'} 
                                                                                        maxHeight="200px"
                                                                                        filename={`option_${['a','b','c','d'][optIdx]}.cpp`}
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                                <PaginationControls 
                                    currentPage={currentMcqPage} 
                                    totalPages={Math.ceil(questions.length / QUESTIONS_PER_PAGE)} 
                                    onPageChange={setCurrentMcqPage} 
                                    totalItems={questions.length}
                                    itemsPerPage={QUESTIONS_PER_PAGE}
                                    label="Questions"
                                />
                            </div>
                        </div>
                    )}

                    {/* Section II: Coding Problems */}
                    {codeQuestions.length > 0 && (
                        <div className="space-y-4">
                            {test.test_type === 'mixed' && (
                                <div className="flex items-center space-x-3 px-1">
                                    <h3 className="text-sm font-bold text-emerald-600 uppercase tracking-widest whitespace-nowrap">Section II: Coding Problems</h3>
                                    <div className="flex-1 h-0.5 rounded-full bg-emerald-600"></div>
                                </div>
                            )}
                            <div className="bg-white rounded overflow-hidden border-l-4 border-l-emerald-600">
                                <div className="divide-y divide-gray-300">
                                    {codeQuestions
                                        .slice((currentCodePage - 1) * QUESTIONS_PER_PAGE, currentCodePage * QUESTIONS_PER_PAGE)
                                        .map((q, idx) => {
                                            const isExpanded = expandedQuestions.has(q.id);
                                            const displayIdx = (currentCodePage - 1) * QUESTIONS_PER_PAGE + idx + 1;
                                            return (
                                                <div key={`code_${q.id}`} className="group">
                                                    {/* Main Row */}
                                                    <div 
                                                        className="flex items-center justify-between px-6 py-4 transition-all cursor-pointer last:border-b-0"
                                                        onClick={() => toggleQuestion(q.id)}
                                                    >
                                                        <div className="flex-1 grid grid-cols-[40px,1fr,100px,120px,80px] items-center gap-4">
                                                            <span className="text-sm font-bold text-emerald-600 text-left">Q{displayIdx}.</span>
                                                            
                                                            <div className="min-w-0 pr-4">
                                                                <span className={`text-sm font-semibold text-foreground text-left truncate block`} title={q.question.title}>
                                                                    {q.question.title}
                                                                </span>
                                                            </div>

                                                            <div className="flex justify-start">
                                                                <span className={`text-xs font-medium capitalize ${q.question.difficulty === 'easy' ? 'text-green-600' : q.question.difficulty === 'medium' ? 'text-yellow-600' : 'text-red-600'}`}>{q.question.difficulty}</span>
                                                            </div>

                                                            <div className="flex justify-start">
                                                                <span className="text-xs font-medium text-muted-foreground capitalize truncate block">{q.question.topic || 'General'}</span>
                                                            </div>

                                                            <div className="flex justify-center">
                                                                <span className="text-sm font-bold text-slate-700">{q.marks} Mark{q.marks > 1 ? 's' : ''}</span>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center space-x-4 ml-6">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteQuestion(q.id, true);
                                                                }}
                                                                disabled={deletingQuestionId === q.id}
                                                                className="text-muted-foreground hover:text-red-600 transition-all"
                                                                title="Remove Question"
                                                            >
                                                                {deletingQuestionId === q.id ? (
                                                                    <div className="w-4 h-4 bg-red-100 animate-pulse rounded" />
                                                                ) : (
                                                                    <FiTrash2 className="w-4 h-4" />
                                                                )}
                                                            </button>
                                                            <div className={`transition-all duration-300 ${isExpanded ? 'rotate-180 text-emerald-600' : 'text-slate-400'}`}>
                                                                <FiChevronDown className="text-xl" />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Expandable Preview */}
                                                    <div className={`grid transition-all duration-300 ease-in-out ${isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                                                        <div className="overflow-hidden">
                                                            <div className="px-6 pb-6 pl-20">
                                                                <div className="mt-4 text-sm text-slate-600 leading-relaxed whitespace-pre-wrap text-left font-medium max-w-4xl">
                                                                    {q.question.description}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                                <PaginationControls 
                                    currentPage={currentCodePage} 
                                    totalPages={Math.ceil(codeQuestions.length / QUESTIONS_PER_PAGE)} 
                                    onPageChange={setCurrentCodePage} 
                                    totalItems={codeQuestions.length}
                                    itemsPerPage={QUESTIONS_PER_PAGE}
                                    label="Problems"
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Add MCQ Modal */}
            {showAddMcq && createPortal(
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-emerald-950/50 backdrop-blur-md overflow-y-auto">
                    <div className="bg-white w-full max-w-2xl mx-auto my-8 flex flex-col max-h-[85vh] shadow-2xl rounded">
                        {/* Header */}
                        <div className="flex items-start justify-between p-6 py-4 bg-emerald-600 text-left rounded-t">
                            <div>
                                <h3 className="text-xl font-bold text-white mb-0.5">Add Multiple Choice Question</h3>
                                {/* <p className="text-xs text-emerald-100">Create a new MCQ with optional code snippet</p> */}
                            </div>
                            <button onClick={() => setShowAddMcq(false)} className="text-emerald-100 hover:text-white transition-colors mt-1">
                                <FiX className="text-2xl" />
                            </button>
                        </div>

                        <div className="p-8 overflow-y-auto flex-1 custom-scrollbar">
                            <form id="add-mcq-form" onSubmit={handleAddMcq} className="space-y-6 text-left">
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-semibold text-emerald-800">Question Text <span className="text-rose-500">*</span></label>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (showQuestionCode) setQuestionCode('');
                                                setShowQuestionCode(!showQuestionCode);
                                            }}
                                            className={`text-xs font-medium tracking-wider px-3 py-1.5 rounded border transition-all ${showQuestionCode ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50'}`}
                                        >
                                            {showQuestionCode ? 'Remove Code Snippet' : 'Add Code Snippet'}
                                        </button>
                                    </div>
                                    <textarea
                                        required
                                        value={questionText}
                                        onChange={(e) => setQuestionText(e.target.value)}
                                        className="w-full bg-accent border-b-2 border-transparent rounded-t text-foreground px-4 py-3 focus:outline-none focus:border-emerald-600 transition-colors duration-200 text-sm font-medium placeholder:text-muted-foreground/40 resize-none h-24"
                                        placeholder="Enter your question here..."
                                    />

                                    {showQuestionCode && (
                                        <div className="mt-4 flex flex-col rounded border border-neutral-700 bg-[#1e1e1e] overflow-hidden shadow-sm font-mono">
                                            <div className="bg-[#1e1e1e] px-2 py-2 border-b border-neutral-700 flex items-center justify-between">
                                                <div className="flex items-center space-x-3">
                                                    <div className="flex space-x-1.5">
                                                        <div className="w-3 h-3 rounded-full bg-[#ff5f56]"></div>
                                                        <div className="w-3 h-3 rounded-full bg-[#ffbd2e]"></div>
                                                        <div className="w-3 h-3 rounded-full bg-[#27c93f]"></div>
                                                    </div>
                                                    <span className="text-sm text-gray-400 font-mono">
                                                        main.cpp
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="h-32 py-1">
                                                <Editor
                                                    height="100%"
                                                    theme="vs-dark"
                                                    defaultLanguage="cpp"
                                                    value={questionCode}
                                                    onChange={(val) => setQuestionCode(val || '')}
                                                    onMount={handleEditorDidMount}
                                                    options={{
                                                        fontSize: 13,
                                                        fontFamily: 'Fira Code, Monaco, Menlo, "Ubuntu Mono", monospace',
                                                        fontLigatures: true,
                                                        minimap: { enabled: false },
                                                        scrollBeyondLastLine: false,
                                                        lineNumbers: 'on',
                                                        lineNumbersMinChars: 3,
                                                        lineDecorationsWidth: 40,
                                                        glyphMargin: false,
                                                        folding: false,
                                                        automaticLayout: true,
                                                        scrollbar: {
                                                            vertical: 'hidden',
                                                            horizontal: 'hidden'
                                                        },
                                                        renderLineHighlight: 'none',
                                                        bracketPairColorization: { enabled: true },
                                                        padding: { top: 16, bottom: 16 }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-semibold text-emerald-800">Answer Options <span className="text-rose-500">*</span></label>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (showOptionCodes) setOptionCodes(['', '', '', '']);
                                                setShowOptionCodes(!showOptionCodes);
                                            }}
                                            className={`text-xs font-medium tracking-wider px-3 py-1.5 rounded transition-all ${showOptionCodes ? 'border bg-emerald-600 border-emerald-600 text-white' : 'bborder bg-white border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50'}`}
                                        >
                                            {showOptionCodes ? 'Remove Code Snippets' : 'Add Code Snippets to Options'}
                                        </button>
                                    </div>

                                    {[
                                        { label: 'A', state: optionA, setter: setOptionA },
                                        { label: 'B', state: optionB, setter: setOptionB },
                                        { label: 'C', state: optionC, setter: setOptionC },
                                        { label: 'D', state: optionD, setter: setOptionD }
                                    ].map((opt, idx) => {
                                        const isCorrect = correctOptionIndex === idx;
                                        return (
                                            <div key={idx} className="space-y-3">
                                                <div
                                                    onClick={() => setCorrectOptionIndex(idx)}
                                                    className={`flex items-center px-4 py-3 text-sm rounded-t transition-all cursor-pointer border-b-2 border-transparent focus-within:border-b-emerald-600 ${isCorrect
                                                            ? 'bg-accent text-emerald-700'
                                                            : 'bg-accent text-slate-700 border border-gray-100'
                                                        }`}
                                                >
                                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 shrink-0 ${isCorrect ? 'bg-emerald-600' : 'bg-slate-400'
                                                        }`}>
                                                        {isCorrect && <FiCheck className="w-3 h-3 text-white" strokeWidth={5} />}
                                                    </div>
                                                    <input
                                                        type="text"
                                                        required
                                                        value={opt.state}
                                                        onChange={(e) => opt.setter(e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className={`flex-1 bg-transparent border-none outline-none p-0 text-sm font-medium placeholder:text-slate-400 ${isCorrect ? 'text-emerald-600 placeholder:text-slate-400' : 'text-slate-700'
                                                            }`}
                                                        placeholder={`Option ${opt.label}`}
                                                    />
                                                </div>

                                                {showOptionCodes && (
                                                    <div className="mt-3 flex flex-col rounded border border-neutral-700 bg-[#1e1e1e] overflow-hidden shadow-sm font-mono">
                                                        <div className="bg-[#1e1e1e] px-2 py-2 border-b border-neutral-700 flex items-center justify-between">
                                                            <div className="flex items-center space-x-3">
                                                                <div className="flex space-x-1.5">
                                                                    <div className="w-3 h-3 rounded-full bg-[#ff5f56]"></div>
                                                                    <div className="w-3 h-3 rounded-full bg-[#ffbd2e]"></div>
                                                                    <div className="w-3 h-3 rounded-full bg-[#27c93f]"></div>
                                                                </div>
                                                                <span className="text-sm text-gray-400 font-mono">
                                                                    option_{opt.label.toLowerCase()}.cpp
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="h-32 py-1">
                                                            <Editor
                                                                height="100%"
                                                                theme="vs-dark"
                                                                defaultLanguage="cpp"
                                                                value={optionCodes[idx]}
                                                                onChange={(val) => {
                                                                    const newCodes = [...optionCodes];
                                                                    newCodes[idx] = val || '';
                                                                    setOptionCodes(newCodes);
                                                                }}
                                                                onMount={handleEditorDidMount}
                                                                options={{
                                                                    fontSize: 13,
                                                                    fontFamily: 'Fira Code, Monaco, Menlo, "Ubuntu Mono", monospace',
                                                                    fontLigatures: true,
                                                                    minimap: { enabled: false },
                                                                    scrollBeyondLastLine: false,
                                                                    lineNumbers: 'on',
                                                                    lineNumbersMinChars: 3,
                                                                    lineDecorationsWidth: 40,
                                                                    glyphMargin: false,
                                                                    folding: false,
                                                                    automaticLayout: true,
                                                                    scrollbar: {
                                                                        vertical: 'hidden',
                                                                        horizontal: 'hidden'
                                                                    },
                                                                    renderLineHighlight: 'none',
                                                                    padding: { top: 16, bottom: 16 }
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-emerald-800 mb-2">Assigned Marks <span className="text-rose-500">*</span></label>
                                    <input
                                        type="number"
                                        min="1"
                                        required
                                        value={marks}
                                        onChange={(e) => setMarks(e.target.value)}
                                        className="w-full md:w-32 bg-accent rounded-t border-b-2 border-transparent text-foreground px-4 py-3 focus:outline-none focus:border-emerald-600 transition-colors duration-200 text-sm font-medium [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        placeholder="1"
                                    />
                                </div>
                            </form>
                        </div>

                        {/* Footer */}
                        <div className="px-4 py-4 border-t border-gray-300 flex justify-end items-center space-x-4 bg-emerald-50/10 rounded-b">
                            <button
                                type="button"
                                onClick={() => setShowAddMcq(false)}
                                disabled={submitting}
                                className="px-6 py-2.5 text-sm font-bold text-muted-foreground hover:text-foreground transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="add-mcq-form"
                                disabled={submitting}
                                className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white transition-all font-semibold rounded flex items-center disabled:opacity-50 text-sm"
                            >
                                {submitting ? "Saving..." : "Save Question"}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}


            {/* Test Results Modal */}
            {
                showResultsModal && (
                    <TestResultsModal
                        test={localTest}
                        onClose={() => setShowResultsModal(false)}
                        onPublishSuccess={(testId) => {
                            setLocalTest(prev => ({ ...prev, results_published: true }));
                        }}
                    />
                )
            }

            {/* Simple wrapper to lazily load QuestionSearchModal to avoid top-level import cycle if any */}
            {showQuestionSearch && (
                <QuestionSearchModalLoader
                    testId={test.id}
                    onClose={() => setShowQuestionSearch(false)}
                    onQuestionAdded={(newCodeQuestion) => {
                        setCodeQuestions([...codeQuestions, newCodeQuestion]);
                        setShowQuestionSearch(false);
                    }}
                />
            )}
        </div>
    );
};

const QuestionSearchModalLoader = (props) => {
    const [ModalCode, setModalCode] = useState(null);
    useEffect(() => {
        import('./QuestionSearchModal').then(mod => setModalCode(() => mod.default));
    }, []);
    if (!ModalCode) return createPortal(
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-emerald-950/20 backdrop-blur-sm overflow-hidden">
            <div className="bg-white rounded w-full max-w-7xl mx-auto my-8 flex flex-col h-[85vh] shadow-2xl overflow-hidden animate-pulse">
                <div className="bg-emerald-100 px-6 py-4 flex items-center justify-between shrink-0 h-16"></div>
                <div className="flex flex-1 overflow-hidden">
                    <div className="flex-1 flex flex-col border-r border-gray-100 overflow-hidden pb-4">
                        <div className="p-8 shrink-0 space-y-4">
                            <div className="h-12 bg-gray-50 rounded" />
                        </div>
                        <div className="flex-1 mx-8 bg-gray-50 rounded h-full" />
                    </div>
                    <div className="w-[450px] shrink-0 bg-white p-8 overflow-y-auto hidden lg:block">
                        <div className="h-full bg-gray-50 rounded" />
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
    return <ModalCode {...props} />;
};

export default TestManager;
